import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, FileText, Eye, Edit3, Trash2, ArrowLeft, Calendar, CheckCircle, Clock, Send, Copy, Upload, Image as ImageIcon, CalendarRange, UserPlus, Users, Mail, X as XIcon, Activity, Archive, ArchiveRestore, Download } from "lucide-react";
import TrialUpgradeChip from "../components/TrialUpgradeChip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

const STATUS_COLORS = {
  draft: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  published: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  sent: "bg-green-500/20 text-green-400 border-green-500/30",
};

const sortCallSheetsByShootDate = (rows) => [...rows].sort((first, second) => {
  const firstDate = first.shoot_date || "";
  const secondDate = second.shoot_date || "";
  if (!firstDate && !secondDate) return 0;
  if (!firstDate) return 1;
  if (!secondDate) return -1;
  return firstDate.localeCompare(secondDate);
});

const callSheetScheduleLabel = (callsheet) => {
  if (!callsheet.shoot_date || !callsheet.day_of_days) {
    return "Unscheduled — Day not set";
  }
  const isoDate = callsheet.shoot_date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const formattedDate = isoDate
    ? `${isoDate[2]}/${isoDate[3]}/${isoDate[1]}`
    : callsheet.shoot_date;
  const dayOfDays = callsheet.day_of_days.replace(/^day\s*/i, "").trim();
  return `${formattedDate} — Day ${dayOfDays}`;
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [callsheets, setCallsheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitingBusy, setInvitingBusy] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState("");
  const [activity, setActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [showShootBlock, setShowShootBlock] = useState(false);
  const [shootBlockForm, setShootBlockForm] = useState({ template_id: "", start_date: "", end_date: "", general_call: "07:00", skip_weekends: false });
  const [generating, setGenerating] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    try {
      const [pRes, csRes, tRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, ax()),
        axios.get(`${API}/projects/${projectId}/callsheets`, ax()),
        axios.get(`${API}/templates`, ax()),
      ]);
      setProject(pRes.data);
      setCallsheets(sortCallSheetsByShootDate(csRes.data));
      setTemplates(tRes.data);
      try {
        const [mRes, iRes, aRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/members`, ax()),
          axios.get(`${API}/projects/${projectId}/invitations`, ax()).catch(() => ({ data: [] })),
          axios.get(`${API}/projects/${projectId}/activity?limit=100`, ax()).catch(() => ({ data: [] })),
        ]);
        setMembers(mRes.data); setInvitations(iRes.data); setActivity(aRes.data);
      } catch {}
    } catch { navigate("/dashboard"); }
    finally { setLoading(false); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInvitingBusy(true); setLastInviteUrl("");
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/invitations`,
        { email: inviteEmail.trim() }, ax());
      setInvitations(prev => prev.some(p => p.id === data.id) ? prev : [data, ...prev]);
      setInviteEmail("");
      const url = `${window.location.origin}/invitations/${data.token}`;
      setLastInviteUrl(url);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send invitation");
    } finally { setInvitingBusy(false); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invitation?")) return;
    await axios.delete(`${API}/projects/${projectId}/invitations/${id}`, ax());
    setInvitations(prev => prev.filter(i => i.id !== id));
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the project?")) return;
    try {
      await axios.post(`${API}/projects/${projectId}/members/${uid}/remove`, {}, ax());
      setMembers(prev => prev.filter(m => m.id !== uid));
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return alert("Logo must be smaller than 2MB");
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/projects/${projectId}/logo`, fd, { withCredentials: true });
      setProject(p => ({ ...p, logo_data_uri: data.logo_data_uri }));
    } catch (e) {
      alert(e.response?.data?.detail || "Logo upload failed");
    } finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove this project's logo? (Falls back to your default logo from Settings.)")) return;
    await axios.delete(`${API}/projects/${projectId}/logo`, ax());
    setProject(p => ({ ...p, logo_data_uri: "" }));
  };

  const generateShootBlock = async () => {
    const { template_id, start_date, end_date, general_call, skip_weekends } = shootBlockForm;
    if (!template_id) return alert("Choose a template first");
    if (!start_date || !end_date) return alert("Pick a start and end date");
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/templates/${template_id}/generate-block`,
        { project_id: projectId, start_date, end_date, general_call, skip_weekends }, ax());
      alert(`Generated ${data.created_count} call sheets. They're now in your project as drafts.`);
      setShowShootBlock(false);
      await fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to generate shoot block");
    } finally { setGenerating(false); }
  };

  const deleteCallsheet = async (id) => {
    if (!window.confirm("Delete this call sheet?")) return;
    await axios.delete(`${API}/callsheets/${id}`, ax());
    setCallsheets(callsheets.filter((c) => c.id !== id));
  };

  const archiveProject = async () => {
    if (!window.confirm("Archive this project? It will be hidden from your active projects but can be restored anytime from the Archived tab.")) return;
    try {
      await axios.post(`${API}/projects/${projectId}/archive`, {}, ax());
      navigate("/dashboard");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to archive project");
    }
  };

  const unarchiveProject = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/unarchive`, {}, ax());
      setProject(p => ({ ...p, archived: false, archived_at: null }));
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to restore project");
    }
  };

  const [exportingCrew, setExportingCrew] = useState(false);
  const exportProjectCrewCSV = async () => {
    setExportingCrew(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/crew/export.csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      // Try to lift the filename out of the header, fall back to a safe default
      let filename = `${(project?.name || "project").replace(/[^\w\-]+/g, "_")}_crew.csv`;
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      const m = cd && cd.match(/filename="?([^"]+)"?/i);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to export crew CSV");
    } finally { setExportingCrew(false); }
  };

  const duplicateCallsheet = async (id) => {
    try {
      const { data } = await axios.post(`${API}/callsheets/${id}/duplicate`, {}, ax());
      setCallsheets((current) => sortCallSheetsByShootDate([...current, data]));
      navigate(`/callsheets/${data.id}/edit`);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to duplicate call sheet");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-amber-400 animate-pulse">Loading...</div></div>;
  if (!project) return null;

  return (
    <div className="min-w-0 space-y-8 p-3 sm:p-6 md:p-8">
      {/* Header */}
      <div>
        <button onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-gray-500 hover:text-amber-400 text-sm mb-4 transition-colors">
          <ArrowLeft size={15} /> Back to Projects
        </button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs text-amber-400/80 uppercase tracking-widest mb-1 font-mono">{project.production_company || "Production"}</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white font-[Outfit] tracking-tight flex items-center gap-3 flex-wrap" data-testid="project-header-title">
              <span>{project.name}</span>
              <TrialUpgradeChip />
            </h1>
          </div>
          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
            {project.archived ? (
              <button
                data-testid="project-unarchive-btn"
                onClick={unarchiveProject}
                className="flex items-center gap-2 border border-green-500/40 text-green-400 hover:bg-green-500/10 px-4 py-2.5 rounded-lg transition-all text-sm"
              >
                <ArchiveRestore size={16} /> Restore
              </button>
            ) : (
              <button
                data-testid="project-archive-btn"
                onClick={archiveProject}
                title="Archive this project"
                className="flex items-center gap-2 border border-[#28303F] text-gray-300 hover:text-amber-400 hover:border-amber-500/40 px-4 py-2.5 rounded-lg transition-all text-sm"
              >
                <Archive size={16} /> Archive
              </button>
            )}
            <button
              data-testid="project-export-crew-csv-btn"
              onClick={exportProjectCrewCSV}
              disabled={exportingCrew}
              title="Download all crew tagged to this project as a CSV"
              className="flex items-center gap-2 border border-[#28303F] text-gray-300 hover:text-amber-400 hover:border-amber-500/40 px-4 py-2.5 rounded-lg transition-all text-sm disabled:opacity-50"
            >
              <Download size={16} /> {exportingCrew ? "Exporting..." : "Export Crew CSV"}
            </button>
            <button
              data-testid="generate-shoot-block-btn"
              onClick={() => setShowShootBlock(true)}
              className="flex items-center gap-2 border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 px-4 py-2.5 rounded-lg transition-all text-sm"
            >
              <CalendarRange size={16} /> Shoot Block
            </button>
            <button
              data-testid="new-callsheet-btn"
              onClick={() => navigate(`/projects/${projectId}/callsheets/new`)}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-5 py-2.5 rounded-lg transition-all text-sm"
            >
              <Plus size={18} /> New Call Sheet
            </button>
          </div>
        </div>

        {project.archived && (
          <div data-testid="project-archived-banner"
            className="mt-4 flex items-center gap-3 bg-amber-500/10 border border-amber-500/40 rounded-lg px-4 py-2.5">
            <Archive size={16} className="text-amber-400 shrink-0" />
            <p className="text-xs text-amber-200">
              This project is <span className="font-bold uppercase tracking-wider">archived</span>
              {project.archived_at && ` on ${new Date(project.archived_at).toLocaleDateString()}`}. It's hidden from your active list — click <span className="font-bold">Restore</span> to bring it back.
            </p>
          </div>
        )}

        {/* Activity moved to bottom of page */}

        {/* Team Members */}
        <div className="mt-3 bg-[#1A1F29] border border-[#28303F] rounded-xl p-4">
          <div className="mb-3 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <div className="text-xs text-gray-500 uppercase tracking-wider min-w-32 flex items-center gap-2"><Users size={12}/> Team</div>
            <p className="text-xs text-gray-500 flex-1 min-w-0">Invite collaborators by email — they'll see this project on their dashboard.</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-3" data-testid="project-members-list">
            {members.map(m => (
              <span key={m.id} data-testid="project-member-chip"
                className="flex items-center gap-2 text-xs bg-[#0D0F12] border border-[#28303F] rounded-full pl-3 pr-1 py-1 text-gray-300">
                <span className={m.role === "owner" ? "text-amber-400" : ""}>{m.email}</span>
                <span className="text-[10px] uppercase tracking-wider text-gray-600 border border-[#28303F] rounded-full px-2 py-0.5">{m.role}</span>
                {m.role !== "owner" && (
                  <button title="Remove" onClick={() => removeMember(m.id)}
                    className="text-gray-600 hover:text-red-400 rounded-full w-5 h-5 flex items-center justify-center leading-none">×</button>
                )}
              </span>
            ))}
            {invitations.map(inv => (
              <span key={inv.id} data-testid="project-invitation-chip"
                className="flex items-center gap-2 text-xs bg-amber-500/5 border border-amber-500/30 rounded-full pl-3 pr-1 py-1 text-amber-300">
                <Mail size={11}/> {inv.email}
                <span className="text-[10px] uppercase tracking-wider text-amber-500/60 border border-amber-500/30 rounded-full px-2 py-0.5">pending</span>
                <button title="Revoke" onClick={() => revokeInvite(inv.id)}
                  className="text-amber-500/60 hover:text-red-400 rounded-full w-5 h-5 flex items-center justify-center leading-none">×</button>
              </span>
            ))}
          </div>

          <div className="flex gap-2 flex-wrap">
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="collaborator@studio.com"
              data-testid="project-invite-email-input"
              className="flex-1 min-w-48 bg-[#0D0F12] border border-[#28303F] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500" />
            <button type="button"
              data-testid="project-invite-send-btn"
              onClick={sendInvite}
              disabled={invitingBusy || !inviteEmail.trim()}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-bold px-4 py-2 rounded text-xs">
              <UserPlus size={13}/> {invitingBusy ? "Inviting..." : "Send Invite"}
            </button>
          </div>

          {lastInviteUrl && (
            <div className="mt-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-2 text-xs">
              <CheckCircle size={13} className="text-green-400 shrink-0"/>
              <span className="text-green-300">Invitation sent. Share this link if the email doesn't arrive:</span>
              <input readOnly value={lastInviteUrl}
                data-testid="project-invite-link"
                onFocus={e => e.target.select()}
                className="flex-1 min-w-32 bg-[#0D0F12] border border-[#28303F] rounded px-2 py-1 text-gray-300 font-mono text-[11px]" />
              <button onClick={() => { navigator.clipboard.writeText(lastInviteUrl); }}
                className="text-amber-400 hover:text-amber-300"><Copy size={13}/></button>
            </div>
          )}
        </div>

        {/* Accent Color Row */}
        <div className="mt-3 flex flex-wrap items-center gap-4 rounded-xl border border-[#28303F] bg-[#1A1F29] p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider min-w-32">Accent Color</div>
          <input
            type="color"
            data-testid="project-accent-color-input"
            value={project.accent_color || "#F59E0B"}
            onChange={async (e) => {
              const color = e.target.value;
              setProject(p => ({ ...p, accent_color: color }));
              try {
                await axios.put(`${API}/projects/${projectId}`,
                  { ...project, accent_color: color, description: project.description || "" }, ax());
              } catch {}
            }}
            className="w-14 h-9 bg-transparent border border-[#28303F] rounded cursor-pointer" />
          <div className="flex-1">
            <p className="text-sm text-white font-mono">{project.accent_color || "#F59E0B"}</p>
            <p className="text-xs text-gray-500 mt-0.5">Used in the PDF header bar and section titles for this production.</p>
          </div>
          <div className="flex gap-1">
            {["#F59E0B", "#EF4444", "#22C55E", "#3B82F6", "#A855F7", "#EC4899", "#0EA5E9", "#111827"].map(c => (
              <button key={c} type="button"
                data-testid="project-accent-swatch"
                onClick={async () => {
                  setProject(p => ({ ...p, accent_color: c }));
                  try {
                    await axios.put(`${API}/projects/${projectId}`,
                      { ...project, accent_color: c, description: project.description || "" }, ax());
                  } catch {}
                }}
                title={c}
                className={`w-6 h-6 rounded-full border-2 ${project.accent_color === c ? "border-white" : "border-[#28303F]"}`}
                style={{ background: c }} />
            ))}
          </div>
        </div>

        {/* Email Identity Row — default From Name + Reply-To for distributions */}
        <div className="mt-3 rounded-xl border border-[#28303F] bg-[#1A1F29] p-4" data-testid="project-email-identity-row">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-xs text-gray-500 uppercase tracking-wider min-w-32 self-center">Call Sheet Email Identity</div>
            <div className="flex-1 min-w-[260px] grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[11px] text-gray-500 uppercase tracking-wider block mb-1">From Name</label>
                <input
                  type="text"
                  data-testid="project-from-name-input"
                  value={project.default_from_name || ""}
                  placeholder="Production Office"
                  onChange={(e) => setProject((p) => ({ ...p, default_from_name: e.target.value }))}
                  onBlur={async (e) => {
                    try {
                      await axios.put(`${API}/projects/${projectId}`,
                        { ...project, default_from_name: e.target.value, description: project.description || "" }, ax());
                    } catch {}
                  }}
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase tracking-wider block mb-1">Reply-To Email</label>
                <input
                  type="email"
                  data-testid="project-reply-to-input"
                  value={project.default_reply_to || ""}
                  placeholder="producer@yourstudio.com"
                  onChange={(e) => setProject((p) => ({ ...p, default_reply_to: e.target.value }))}
                  onBlur={async (e) => {
                    try {
                      await axios.put(`${API}/projects/${projectId}`,
                        { ...project, default_reply_to: e.target.value, description: project.description || "" }, ax());
                    } catch {}
                  }}
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-gray-600 mt-2">
            Recipients see this name in their inbox, and replies route to the Reply-To address. Overridable on each distribution.
          </p>
        </div>

        {/* Project Logo Row */}
        <div className="mt-3 flex flex-wrap items-center gap-4 rounded-xl border border-[#28303F] bg-[#1A1F29] p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider min-w-32">Project Logo</div>
          {project.logo_data_uri ? (
            <div className="flex items-center gap-4 flex-1">
              <div className="bg-white rounded p-1.5 flex items-center justify-center" style={{ minWidth: "90px", height: "48px" }}>
                <img data-testid="project-logo-preview" src={project.logo_data_uri} alt="Project logo" style={{ maxHeight: "40px", maxWidth: "120px", objectFit: "contain" }} />
              </div>
              <p className="text-xs text-gray-500 flex-1">
                Default header logo on every call sheet in this project. Any call sheet can upload its own override in the builder.
              </p>
              <button type="button" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                className="text-xs text-amber-400 hover:text-amber-300 border border-amber-500/30 px-3 py-1.5 rounded-lg">
                {uploadingLogo ? "..." : "Replace"}
              </button>
              <button type="button" onClick={removeLogo}
                data-testid="project-logo-remove-btn"
                className="text-gray-500 hover:text-red-400 p-1.5">
                <Trash2 size={15} />
              </button>
            </div>
          ) : (
            <button type="button" data-testid="project-logo-upload-btn"
              onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
              className="flex-1 flex items-center justify-center gap-2 border border-dashed border-[#28303F] hover:border-amber-500/50 rounded-lg py-3 text-gray-500 hover:text-amber-400 text-sm transition-all">
              <Upload size={14} /> {uploadingLogo ? "Uploading..." : "Upload project-specific logo (optional)"}
            </button>
          )}
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            data-testid="project-logo-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
            className="hidden" />

          {project.logo_data_uri && (
            <div className="mt-1 w-full flex flex-col gap-3 border-t border-[#28303F] pt-3 sm:flex-row sm:items-center sm:gap-6" data-testid="project-logo-layout-controls">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Alignment</span>
                <div className="flex gap-1 rounded border border-[#28303F] bg-[#0D0F12] p-0.5">
                  {["left", "center", "right"].map((pos) => {
                    const active = (project.logo_align || "center") === pos;
                    return (
                      <button
                        key={pos}
                        type="button"
                        data-testid={`project-logo-align-${pos}`}
                        onClick={async () => {
                          setProject((p) => ({ ...p, logo_align: pos }));
                          try {
                            await axios.put(`${API}/projects/${projectId}`,
                              { ...project, logo_align: pos, description: project.description || "" }, ax());
                          } catch {}
                        }}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded ${
                          active ? "bg-amber-500 text-black font-bold" : "text-gray-400 hover:text-white"
                        }`}
                      >
                        {pos}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-1 items-center gap-3 sm:min-w-[220px]">
                <span className="text-xs text-gray-500 uppercase tracking-wider shrink-0">Max Height</span>
                <input
                  type="range"
                  min="20"
                  max="60"
                  step="2"
                  value={project.logo_max_height_pt || 36}
                  data-testid="project-logo-max-height-slider"
                  onChange={(e) => setProject((p) => ({ ...p, logo_max_height_pt: Number(e.target.value) }))}
                  onMouseUp={async (e) => {
                    try {
                      await axios.put(`${API}/projects/${projectId}`,
                        { ...project, logo_max_height_pt: Number(e.target.value), description: project.description || "" }, ax());
                    } catch {}
                  }}
                  onTouchEnd={async (e) => {
                    const v = Number(e.target.value);
                    try {
                      await axios.put(`${API}/projects/${projectId}`,
                        { ...project, logo_max_height_pt: v, description: project.description || "" }, ax());
                    } catch {}
                  }}
                  className="flex-1 accent-amber-500"
                />
                <span className="font-mono text-xs text-amber-400 w-14 text-right" data-testid="project-logo-max-height-value">
                  {project.logo_max_height_pt || 36}pt
                </span>
              </div>
              <p className="text-[11px] text-gray-600 w-full sm:w-auto">
                Lower for wide logos · higher for tall logos. 36pt is the standard default.
              </p>
            </div>
          )}
        </div>

        {/* Project Info Bar */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-500">
          {project.director && <span><span className="text-gray-600">Director:</span> {project.director}</span>}
          {project.producer && <span><span className="text-gray-600">Producer:</span> {project.producer}</span>}
          {project.first_ad && <span><span className="text-gray-600">1st AD:</span> {project.first_ad}</span>}
          {project.unit_pm && <span><span className="text-gray-600">UPM:</span> {project.unit_pm}</span>}
        </div>
      </div>

      {/* Call Sheets List */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4 font-[Outfit]">Call Sheets ({callsheets.length})</h2>
        {callsheets.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#28303F] rounded-2xl">
            <FileText size={36} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 font-medium">No call sheets yet</p>
            <p className="text-gray-600 text-sm mt-1">Create your first call sheet for this production</p>
            <button onClick={() => navigate(`/projects/${projectId}/callsheets/new`)}
              className="mt-5 bg-amber-500 hover:bg-amber-600 text-black font-bold px-5 py-2.5 rounded-lg text-sm transition-all">
              <Plus size={15} className="inline mr-1.5" />New Call Sheet
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {callsheets.map((cs) => (
              <div key={cs.id} data-testid="callsheet-row-item"
                className="group flex flex-col gap-4 rounded-xl border border-[#28303F] bg-[#1A1F29] p-5 transition-colors hover:border-amber-500/30 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-[#0D0F12] rounded-lg">
                    <FileText size={18} className="text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 data-testid="callsheet-schedule-label" className="font-semibold text-white text-sm">
                        {callSheetScheduleLabel(cs)}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[cs.status] || STATUS_COLORS.draft}`}>
                        {cs.status || "draft"}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {cs.general_call && <span className="flex items-center gap-1"><Clock size={11} /> Call: {cs.general_call}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                  <button onClick={() => navigate(`/callsheets/${cs.id}/preview`)}
                    data-testid="view-callsheet-btn"
                    className="p-2 text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all" title="Preview">
                    <Eye size={16} />
                  </button>
                  <button onClick={() => navigate(`/callsheets/${cs.id}/edit`)}
                    className="p-2 text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all" title="Edit">
                    <Edit3 size={16} />
                  </button>
                  <button onClick={() => duplicateCallsheet(cs.id)}
                    data-testid="duplicate-callsheet-btn"
                    className="p-2 text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all" title="Duplicate">
                    <Copy size={16} />
                  </button>
                  <button onClick={() => deleteCallsheet(cs.id)}
                    className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all" title="Delete">
                    <Trash2 size={16} />
                  </button>
                  {(cs.status || "draft") === "draft" ? (
                    <button onClick={() => navigate(`/callsheets/${cs.id}/edit`)}
                      data-testid="callsheet-row-primary-action"
                      className="flex items-center gap-1.5 bg-[#28303F] hover:bg-[#323B4D] text-white border border-[#28303F] px-3 py-1.5 rounded-lg text-xs font-medium transition-all">
                      <Edit3 size={12} /> Edit
                    </button>
                  ) : (
                    <button onClick={() => navigate(`/callsheets/${cs.id}/distribute`)}
                      data-testid="callsheet-row-primary-action"
                      className="flex items-center gap-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-all">
                      <Send size={12} /> Distribute
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activity Log (bottom, collapsed by default) */}
      <div className="bg-[#1A1F29] border border-[#28303F] rounded-xl overflow-hidden" data-testid="project-activity-section">
        <button
          type="button"
          onClick={() => setActivityOpen(o => !o)}
          data-testid="project-activity-toggle"
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#1E2530] transition-colors"
        >
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-amber-400/80" />
            <h3 className="text-sm font-semibold text-white uppercase tracking-widest font-mono">Activity</h3>
            <span className="text-xs text-gray-500 ml-1">{activity.length} event{activity.length === 1 ? "" : "s"}</span>
          </div>
          <span className="text-xs text-gray-500">{activityOpen ? "Hide" : "Show"}</span>
        </button>
        {activityOpen && (
          <div className="px-5 pb-5 border-t border-[#28303F] pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500">Who did what and when across this project.</p>
              <button type="button"
                onClick={async () => {
                  const { data } = await axios.get(`${API}/projects/${projectId}/activity?limit=100`, ax());
                  setActivity(data);
                }}
                className="text-xs text-amber-400 hover:text-amber-300">Refresh</button>
            </div>
            {activity.length === 0 ? (
              <p className="text-xs text-gray-600">No activity yet — edits, invites, and call-sheet actions will show up here.</p>
            ) : (
              <div className={`space-y-1.5 overflow-y-auto pr-2 ${activityExpanded ? "max-h-[520px]" : "max-h-60"}`}>
                {activity.map((a, i) => (
                  <div key={a.id || i} data-testid="project-activity-row"
                    className="flex items-start gap-3 text-xs bg-[#0D0F12] border border-[#28303F] rounded px-3 py-2">
                    <span className="text-amber-400 font-mono text-[10px] mt-0.5 shrink-0">
                      {new Date(a.timestamp).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className="text-white truncate font-medium shrink-0 min-w-32 max-w-48">{a.user_email || "system"}</span>
                    <span className="text-gray-400 flex-1 min-w-0">{a.summary}</span>
                  </div>
                ))}
              </div>
            )}
            {activity.length > 5 && (
              <button
                onClick={() => setActivityExpanded(!activityExpanded)}
                className="mt-2 text-[11px] text-gray-500 hover:text-amber-400 transition-colors">
                {activityExpanded ? "Collapse" : `Show all ${activity.length}`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Shoot Block Modal */}
      {showShootBlock && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#28303F]">
              <div>
                <h3 className="font-bold text-white font-[Outfit]">Generate Shoot Block</h3>
                <p className="text-xs text-gray-500 mt-0.5">Auto-create one draft call sheet per shoot day, pre-filled from a template.</p>
              </div>
              <button onClick={() => setShowShootBlock(false)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              {templates.length === 0 ? (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-300 text-xs">
                  You need at least one saved template first. Open the Call Sheet Builder, set your defaults, and click "Save as Template".
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Template *</label>
                    <select value={shootBlockForm.template_id}
                      onChange={e => setShootBlockForm(f => ({ ...f, template_id: e.target.value }))}
                      data-testid="shoot-block-template-select"
                      className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500">
                      <option value="">Choose a template…</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Start Date *</label>
                      <input type="date" value={shootBlockForm.start_date}
                        onChange={e => setShootBlockForm(f => ({ ...f, start_date: e.target.value }))}
                        data-testid="shoot-block-start-date"
                        className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">End Date *</label>
                      <input type="date" value={shootBlockForm.end_date}
                        onChange={e => setShootBlockForm(f => ({ ...f, end_date: e.target.value }))}
                        data-testid="shoot-block-end-date"
                        className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">General Call Time</label>
                    <input value={shootBlockForm.general_call}
                      onChange={e => setShootBlockForm(f => ({ ...f, general_call: e.target.value }))}
                      placeholder="07:00"
                      className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
                    <input type="checkbox" checked={shootBlockForm.skip_weekends}
                      onChange={e => setShootBlockForm(f => ({ ...f, skip_weekends: e.target.checked }))}
                      data-testid="shoot-block-skip-weekends"
                      className="accent-amber-500" />
                    Skip Saturdays & Sundays
                  </label>
                </>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowShootBlock(false)}
                  className="flex-1 border border-[#28303F] text-gray-400 hover:text-white py-2.5 rounded-lg text-sm">Cancel</button>
                <button onClick={generateShootBlock}
                  disabled={generating || templates.length === 0}
                  data-testid="shoot-block-generate-btn"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-bold py-2.5 rounded-lg text-sm">
                  {generating ? "Generating..." : "Generate Call Sheets"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
